import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Calendar, Clock, UserCheck, UserX, LogIn, LogOut,
  ChevronLeft, ChevronRight, Loader2, MapPin, IndianRupee, Edit, CalendarOff, Users, Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown,
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import { usersAPI, attendanceAPI, manpowerAPI } from '../services/api';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Attendance = () => {
  const { user, isAdmin } = useAuth();
  const isTeamLead = user?.role === 'teamlead';
  const canMarkAttendance = isAdmin || isTeamLead;
  
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedDate, setSelectedDate] = useState(currentDate.toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [manpowerRecords, setManpowerRecords] = useState([]); // Manpower entries
  const [loading, setLoading] = useState(true);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [punchTime, setPunchTime] = useState(null);
  
  // Mark Attendance Dialog
  const [markDialog, setMarkDialog] = useState({ open: false, employee: null });
  const [markData, setMarkData] = useState({
    status: 'present',
    conveyance: 0,
    location: 'Office'
  });
  const [submitting, setSubmitting] = useState(false);

  // Bulk entry mode - fill attendance for all employees at once
  const [bulkEntryMode, setBulkEntryMode] = useState(false);
  const [bulkEntries, setBulkEntries] = useState({}); // { empId: { status, conveyance, location } }
  const [bulkSaving, setBulkSaving] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Selected employee IDs for bulk save

  // Admin Manpower Dialog - Multi-row
  const [teamLeads, setTeamLeads] = useState([]);
  const [adminManpowerDialog, setAdminManpowerDialog] = useState(false);
  const emptyManpowerRow = () => ({ name: '', team_lead_id: '', location: '', daily_earned: 500, conveyance: 0, date: '' });
  const [manpowerRows, setManpowerRows] = useState([emptyManpowerRow()]);
  const [addingManpower, setAddingManpower] = useState(false);

  // Sort employees alphabetically
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Excel Upload State
  const [uploadAttendanceDialog, setUploadAttendanceDialog] = useState(false);
  const [uploadManpowerDialog, setUploadManpowerDialog] = useState(false);
  const [uploadingAttendance, setUploadingAttendance] = useState(false);
  const [uploadingManpower, setUploadingManpower] = useState(false);
  const [attendanceUploadResult, setAttendanceUploadResult] = useState(null);
  const [manpowerUploadResult, setManpowerUploadResult] = useState(null);

  const todayStr = currentDate.toISOString().split('T')[0];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    const newDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
    setSelectedDate(newDate);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear]);

  // Load team leads for Admin manpower dialog
  useEffect(() => {
    if (isAdmin) {
      loadTeamLeads();
    }
  }, [isAdmin]);

  const loadTeamLeads = async () => {
    try {
      const allUsers = await usersAPI.getAll('teamlead', 'active');
      setTeamLeads(allUsers || []);
    } catch (error) {
      console.error('Error loading team leads:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let usersData;
      if (isTeamLead) {
        usersData = await fetch(`${API_URL}/api/users/team/${user.id}`).then(r => r.json());
      } else {
        usersData = await usersAPI.getAll();
      }
      const activeEmployees = usersData.filter(u => u.status === 'active' && u.role !== 'admin');
      setEmployees(activeEmployees);

      const attendanceData = await attendanceAPI.getAll(null, null, selectedMonth, selectedYear);
      setAttendanceRecords(attendanceData || []);
      
      // Load manpower records for Admin view
      if (isAdmin) {
        try {
          const manpowerData = await manpowerAPI.getByMonth(selectedYear, selectedMonth);
          setManpowerRecords(manpowerData || []);
        } catch (err) {
          console.error('Error loading manpower:', err);
          setManpowerRecords([]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  // Get attendance for selected date
  const dateAttendanceUnsorted = employees.map(emp => {
    const record = attendanceRecords.find(r => r.emp_id === emp.id && r.date === selectedDate);
    return {
      ...emp,
      attendance: record ? {
        status: record.status || 'present',
        punchIn: record.punch_in || null,
        punchOut: record.punch_out || null,
        workHours: record.work_hours || 0,
        attendanceStatus: record.attendance_status || 'full_day',
        location: record.location || '',
        conveyance: record.conveyance_amount || 0,
        dailyDuty: Math.round(record.daily_duty_amount || 0)
      } : { 
        status: 'not-marked', 
        punchIn: null, 
        punchOut: null, 
        workHours: 0,
        attendanceStatus: null,
        location: '',
        conveyance: 0,
        dailyDuty: 0
      }
    };
  });

  // Sort employees alphabetically by name
  const dateAttendance = [...dateAttendanceUnsorted].sort((a, b) => {
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    if (sortOrder === 'asc') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });

  const presentCount = dateAttendance.filter(e => e.attendance.attendanceStatus === 'full_day').length;
  const halfDayCount = dateAttendance.filter(e => e.attendance.attendanceStatus === 'half_day').length;
  const absentCount = dateAttendance.filter(e => e.attendance.attendanceStatus === 'absent').length;
  const leaveCount = dateAttendance.filter(e => e.attendance.attendanceStatus === 'leave').length;
  const notMarkedCount = dateAttendance.filter(e => e.attendance.status === 'not-marked').length;

  // Open mark attendance dialog
  const openMarkDialog = (emp) => {
    const existing = attendanceRecords.find(r => r.emp_id === emp.id && r.date === selectedDate);
    setMarkData({
      status: existing?.attendance_status || 'full_day',
      conveyance: existing?.conveyance_amount ?? 0,
      location: existing?.location || 'Office'
    });
    setMarkDialog({ open: true, employee: emp });
  };

  // Submit attendance with conveyance and location
  const submitAttendance = async () => {
    if (!markDialog.employee) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        emp_id: markDialog.employee.id,
        date: selectedDate,
        status: markData.status,
        marked_by: user?.id || 'ADMIN001',
        conveyance: markData.conveyance.toString(),
        location: markData.location
      });
      
      const response = await fetch(`${API_URL}/api/attendance/mark?${params}`, { method: 'POST' });
      const result = await response.json();
      
      if (response.ok) {
        toast.success(`Attendance marked: ${markData.status} | ₹${Math.round(result.daily_duty_amount || 0)} duty + ₹${markData.conveyance} conv`);
        setMarkDialog({ open: false, employee: null });
        loadData();
      } else {
        toast.error(result.detail || 'Failed to mark attendance');
      }
    } catch (error) {
      toast.error('Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  // Initialize bulk entries when entering bulk mode or changing date
  const initializeBulkEntries = () => {
    const entries = {};
    employees.forEach(emp => {
      const existing = attendanceRecords.find(r => r.emp_id === emp.id && r.date === selectedDate);
      entries[emp.id] = {
        status: existing?.attendance_status || 'full_day',
        conveyance: existing?.conveyance_amount ?? 0,
        location: existing?.location || 'Office'
      };
    });
    setBulkEntries(entries);
    // Select all employees by default
    setSelectedEmployees(employees.map(emp => emp.id));
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (empId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(empId)) {
        return prev.filter(id => id !== empId);
      }
      return [...prev, empId];
    });
  };

  // Select/Deselect all employees
  const toggleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(emp => emp.id));
    }
  };

  // Update bulk entry for a specific employee
  const updateBulkEntry = (empId, field, value) => {
    setBulkEntries(prev => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [field]: value
      }
    }));
  };

  // Apply same values to all employees
  const applyToAll = (field, value) => {
    setBulkEntries(prev => {
      const updated = {};
      Object.keys(prev).forEach(empId => {
        updated[empId] = { ...prev[empId], [field]: value };
      });
      return updated;
    });
  };

  // Save all bulk entries - only for selected employees
  const saveBulkEntries = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    // Only save selected employees
    const entriesToSave = Object.entries(bulkEntries).filter(
      ([empId, data]) => data.status && selectedEmployees.includes(empId)
    );
    
    if (entriesToSave.length === 0) {
      toast.error('No entries to save');
      return;
    }

    setBulkSaving(true);
    let successCount = 0;
    let failCount = 0;

    for (const [empId, data] of entriesToSave) {
      try {
        const params = new URLSearchParams({
          emp_id: empId,
          date: selectedDate,
          status: data.status,
          marked_by: user?.id || 'ADMIN001',
          conveyance: data.conveyance.toString(),
          location: data.location
        });
        
        const response = await fetch(`${API_URL}/api/attendance/mark?${params}`, { method: 'POST' });
        
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    setBulkSaving(false);
    
    if (successCount > 0) {
      toast.success(`Successfully saved ${successCount} attendance records`);
      setBulkEntryMode(false);
      setBulkEntries({});
      setSelectedEmployees([]);
      loadData();
    }
    if (failCount > 0) {
      toast.error(`Failed to save ${failCount} records`);
    }
  };

  // Count selected entries for save button
  const selectedEntriesCount = selectedEmployees.length;

  const handlePunchIn = () => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    setPunchTime(timeStr);
    setIsPunchedIn(true);
    toast.success('Punched in successfully');
  };

  const handlePunchOut = () => {
    setIsPunchedIn(false);
    toast.success('Punched out successfully');
  };

  // Open Admin Manpower Dialog
  const openAdminManpowerDialog = () => {
    setManpowerRows([{ ...emptyManpowerRow(), date: selectedDate }]);
    setAdminManpowerDialog(true);
  };

  // Update a specific row
  const updateManpowerRow = (index, field, value) => {
    setManpowerRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  // Add new empty row
  const addManpowerRow = () => {
    const lastRow = manpowerRows[manpowerRows.length - 1];
    setManpowerRows(prev => [...prev, { 
      ...emptyManpowerRow(), 
      date: lastRow?.date || selectedDate,
      team_lead_id: lastRow?.team_lead_id || '',
      location: lastRow?.location || '',
      daily_earned: lastRow?.daily_earned || 500,
      conveyance: lastRow?.conveyance || 0
    }]);
  };

  // Remove a row
  const removeManpowerRow = (index) => {
    if (manpowerRows.length <= 1) return;
    setManpowerRows(prev => prev.filter((_, i) => i !== index));
  };

  // Submit Admin Manpower Entries (bulk)
  const submitAdminManpower = async () => {
    // Validate all rows
    const validRows = manpowerRows.filter(row => row.name.trim());
    if (validRows.length === 0) {
      toast.error('Please fill at least one manpower entry');
      return;
    }
    
    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      if (!row.team_lead_id) { toast.error(`Row ${i + 1}: Select a Team Lead`); return; }
      if (!row.location.trim()) { toast.error(`Row ${i + 1}: Enter location`); return; }
      if (row.daily_earned <= 0) { toast.error(`Row ${i + 1}: Daily earned must be > 0`); return; }
      if (!row.date) { toast.error(`Row ${i + 1}: Select a date`); return; }
    }

    setAddingManpower(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const row of validRows) {
      try {
        await manpowerAPI.adminAdd(row);
        successCount++;
      } catch (error) {
        failCount++;
        console.error('Error adding manpower:', error);
      }
    }
    
    setAddingManpower(false);
    if (successCount > 0) {
      toast.success(`${successCount} manpower entries added!`);
      setAdminManpowerDialog(false);
      loadData();
    }
    if (failCount > 0) {
      toast.error(`${failCount} entries failed`);
    }
  };

  // Delete Manpower Entry (Admin only)
  const handleDeleteManpower = async (manpowerId) => {
    if (!window.confirm('Are you sure you want to delete this manpower entry?')) {
      return;
    }
    try {
      await manpowerAPI.adminDelete(manpowerId);
      toast.success('Manpower entry deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete manpower entry');
    }
  };

  const getDaysInMonth = (month, year) => new Date(year, month, 0).getDate();

  // Excel Upload Handlers
  const handleDownloadAttendanceTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/attendance/download-template`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'attendance_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleDownloadManpowerTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manpower/download-template`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'manpower_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUploadAttendance = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported');
      return;
    }
    setUploadingAttendance(true);
    setAttendanceUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `${API_URL}/api/attendance/upload-excel?marked_by=${user?.id || 'ADMIN001'}`,
        { method: 'POST', body: formData }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Upload failed');
      setAttendanceUploadResult(result);
      const marked = result.marked || 0;
      const updated = result.updated || 0;
      if (marked > 0 || updated > 0) {
        const parts = [];
        if (marked > 0) parts.push(`${marked} marked`);
        if (updated > 0) parts.push(`${updated} conveyance updated`);
        toast.success(parts.join(', ') + '!');
        loadData();
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploadingAttendance(false);
      e.target.value = '';
    }
  };

  const handleUploadManpower = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Only .xlsx files are supported');
      return;
    }
    setUploadingManpower(true);
    setManpowerUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/manpower/upload-excel`, {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Upload failed');
      setManpowerUploadResult(result);
      if (result.added > 0) {
        toast.success(`${result.added} manpower entries added!`);
        loadData();
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploadingManpower(false);
      e.target.value = '';
    }
  };
  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const monthDates = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    return `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2">Loading attendance data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Attendance</h1>
          <p className="text-sm text-gray-500">Track daily attendance</p>
        </div>
        {!canMarkAttendance && (
          <div className="flex gap-2">
            {!isPunchedIn ? (
              <Button onClick={handlePunchIn} className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none">
                <LogIn size={16} className="mr-2" /> Punch In
              </Button>
            ) : (
              <Button onClick={handlePunchOut} className="bg-red-600 hover:bg-red-700 flex-1 sm:flex-none">
                <LogOut size={16} className="mr-2" /> Punch Out
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Employee Self View */}
      {!canMarkAttendance && (
        <Card className="bg-gradient-to-r from-[#1E2A5E] to-[#2D3A8C] text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Today&apos;s Status</p>
                <h2 className="text-2xl sm:text-3xl font-bold">
                  {isPunchedIn ? 'Working' : punchTime ? 'Completed' : 'Not Started'}
                </h2>
                {punchTime && <p className="text-white/70 text-sm mt-2">Punched in at {punchTime}</p>}
              </div>
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center">
                <Clock size={32} className="text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin/Team Lead View */}
      {canMarkAttendance && (
        <>
          {/* Stats - Mobile Responsive */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
            <Card>
              <CardContent className="p-2 sm:p-3 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserCheck size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500">Full Day</p>
                  <p className="text-base sm:text-xl font-bold text-green-600">{presentCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-2 sm:p-3 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock size={16} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500">Half Day</p>
                  <p className="text-base sm:text-xl font-bold text-yellow-600">{halfDayCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-2 sm:p-3 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CalendarOff size={16} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500">Leave</p>
                  <p className="text-base sm:text-xl font-bold text-orange-600">{leaveCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-2 sm:p-3 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <UserX size={16} className="text-red-600" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500">Absent</p>
                  <p className="text-base sm:text-xl font-bold text-red-600">{absentCount}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-2 sm:p-3 flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar size={16} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs text-gray-500">Not Marked</p>
                  <p className="text-base sm:text-xl font-bold text-gray-600">{notMarkedCount}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Month/Year Selector + Calendar */}
          <Card>
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-28 sm:w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-20 sm:w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                
                {/* Excel Upload Buttons */}
                {isAdmin && (
                  <div className="flex gap-1.5 ml-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setAttendanceUploadResult(null); setUploadAttendanceDialog(true); }}
                      className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="upload-attendance-btn"
                    >
                      <FileSpreadsheet size={14} className="mr-1" />
                      <span className="hidden sm:inline">Upload Attendance</span>
                      <span className="sm:hidden">Attendance</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setManpowerUploadResult(null); setUploadManpowerDialog(true); }}
                      className="h-8 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                      data-testid="upload-manpower-btn"
                    >
                      <FileSpreadsheet size={14} className="mr-1" />
                      <span className="hidden sm:inline">Upload Manpower</span>
                      <span className="sm:hidden">Manpower</span>
                    </Button>
                  </div>
                )}
                
                <span className="text-xs text-gray-500 ml-auto">Not Marked: {notMarkedCount}</span>
              </div>
              
              {/* Mini Calendar */}
              <div className="grid grid-cols-7 gap-1 text-center text-xs">
                {['S','M','T','W','T','F','S'].map((d, i) => (
                  <div key={i} className="p-1 font-medium text-gray-500">{d}</div>
                ))}
                {/* Empty cells for first day offset */}
                {Array.from({ length: new Date(selectedYear, selectedMonth - 1, 1).getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {monthDates.map((date, idx) => {
                  const day = idx + 1;
                  const dayRecord = attendanceRecords.find(r => r.date === date);
                  const hasAttendance = !!dayRecord;
                  const isSelected = date === selectedDate;
                  const isToday = date === todayStr;
                  const isSunday = new Date(date).getDay() === 0;
                  
                  // Get attendance status for coloring
                  const getCalendarColor = () => {
                    if (isSelected) return 'bg-blue-600 text-white';
                    if (isToday) return 'ring-2 ring-blue-600';
                    if (isSunday) return 'bg-blue-50 text-blue-500';
                    if (!hasAttendance) return 'hover:bg-gray-100 text-gray-400';
                    
                    const status = dayRecord?.attendance_status || dayRecord?.status;
                    switch(status) {
                      case 'full_day':
                      case 'present':
                        return 'bg-green-500 text-white';
                      case 'half_day':
                        return 'bg-yellow-500 text-white';
                      case 'absent':
                        return 'bg-red-500 text-white';
                      case 'leave':
                        return 'bg-orange-500 text-white';
                      default:
                        return 'bg-gray-100 text-gray-600';
                    }
                  };
                  
                  return (
                    <button
                      key={date}
                      onClick={() => {
                        setSelectedDate(date);
                        if (bulkEntryMode) {
                          setBulkEntryMode(false);
                          setBulkEntries({});
                        }
                      }}
                      className={`p-1 sm:p-2 text-xs sm:text-sm rounded-lg transition-all ${getCalendarColor()}`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              
              {/* Calendar Legend */}
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t text-[10px]">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-500 rounded"></div>
                  <span>Present</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-yellow-500 rounded"></div>
                  <span>Half</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-red-500 rounded"></div>
                  <span>Absent</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-orange-500 rounded"></div>
                  <span>Leave</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attendance List - Mobile Cards / Desktop Table */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base sm:text-lg">
                    {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </CardTitle>
                  {/* Sort Toggle Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="h-8 px-2"
                    title={sortOrder === 'asc' ? 'Sorted A-Z (Click for Z-A)' : 'Sorted Z-A (Click for A-Z)'}
                  >
                    {sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                    <span className="ml-1 text-xs">{sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
                  </Button>
                </div>
                <div className="flex gap-2">
                  {!bulkEntryMode ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setBulkEntryMode(true);
                        initializeBulkEntries();
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="bulk-entry-mode-btn"
                    >
                      <Edit size={14} className="mr-1" /> Bulk Entry Mode
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setBulkEntryMode(false);
                          setBulkEntries({});
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveBulkEntries}
                        disabled={bulkSaving || selectedEntriesCount === 0}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="save-all-attendance-btn"
                      >
                        {bulkSaving ? (
                          <><Loader2 size={14} className="mr-1 animate-spin" /> Saving...</>
                        ) : (
                          <>Save Selected ({selectedEntriesCount})</>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {bulkEntryMode && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                  <p><strong>Bulk Entry Mode:</strong> Select employees using checkboxes, fill details, then click &quot;Save Selected&quot; to save only selected employees.</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={toggleSelectAll} className="h-6 text-xs">
                      {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => applyToAll('status', 'full_day')} className="h-6 text-xs">
                      All Full Day
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => applyToAll('status', 'half_day')} className="h-6 text-xs">
                      All Half Day
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => applyToAll('status', 'absent')} className="h-6 text-xs">
                      All Absent
                    </Button>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-2 sm:p-4">
              {/* Mobile View - Cards */}
              <div className="sm:hidden space-y-3">
                {dateAttendance.map((emp) => {
                  const bulkEntry = bulkEntries[emp.id] || {};
                  const isSelected = selectedEmployees.includes(emp.id);
                  return (
                    <div key={emp.id} className={`p-3 bg-gray-50 rounded-lg ${bulkEntryMode && !isSelected ? 'opacity-50' : ''}`} data-testid={`attendance-card-${emp.id}`}>
                      {/* Employee Info Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {bulkEntryMode && (
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleEmployeeSelection(emp.id)}
                              className="mr-1"
                            />
                          )}
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {emp.name?.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{emp.name}</p>
                            <p className="text-xs text-gray-500">{emp.id}</p>
                          </div>
                        </div>
                        {bulkEntryMode ? (
                          <Select 
                            value={bulkEntry.status || 'full_day'} 
                            onValueChange={(v) => updateBulkEntry(emp.id, 'status', v)}
                          >
                            <SelectTrigger className="w-24 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full_day">Full Day</SelectItem>
                              <SelectItem value="half_day">Half Day</SelectItem>
                              <SelectItem value="leave">Leave</SelectItem>
                              <SelectItem value="absent">Absent</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ml-2 ${
                            emp.attendance.attendanceStatus === 'full_day' ? 'bg-green-100 text-green-700' :
                            emp.attendance.attendanceStatus === 'half_day' ? 'bg-yellow-100 text-yellow-700' :
                            emp.attendance.attendanceStatus === 'leave' ? 'bg-blue-100 text-blue-700' :
                            emp.attendance.attendanceStatus === 'absent' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {emp.attendance.status === 'not-marked' ? 'Not Marked' : 
                             emp.attendance.attendanceStatus === 'half_day' ? 'Half' :
                             emp.attendance.attendanceStatus === 'leave' ? 'Leave' :
                             emp.attendance.attendanceStatus === 'absent' ? 'Absent' : 'Full'}
                          </span>
                        )}
                      </div>
                      
                      {/* Stats Row or Editable Fields */}
                      {bulkEntryMode ? (
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div>
                            <Label className="text-[10px] text-gray-500">Conveyance</Label>
                            <Input 
                              type="number" 
                              value={bulkEntry.conveyance || 0}
                              onChange={(e) => updateBulkEntry(emp.id, 'conveyance', parseFloat(e.target.value) || 0)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-500">Location</Label>
                            <Input 
                              type="text" 
                              value={bulkEntry.location || ''}
                              onChange={(e) => updateBulkEntry(emp.id, 'location', e.target.value)}
                              className="h-8"
                              placeholder="Office"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mb-3 bg-white p-2 rounded-md">
                          <div className="text-center">
                            <p className="text-gray-400 text-[10px]">Duty</p>
                            <p className="font-semibold text-green-600">₹{emp.attendance.dailyDuty.toLocaleString()}</p>
                          </div>
                          <div className="text-center border-x border-gray-100">
                            <p className="text-gray-400 text-[10px]">Conv</p>
                            <p className="font-semibold text-blue-600">₹{emp.attendance.conveyance}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-400 text-[10px]">Location</p>
                            <p className="font-semibold truncate">{emp.attendance.location || '-'}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons - Only show when not in bulk mode */}
                      {!bulkEntryMode && (
                        <div className="grid grid-cols-4 gap-1">
                          <Button size="sm" variant="outline" onClick={() => openMarkDialog(emp)} className="text-blue-600 h-8 px-2">
                            <Edit size={12} className="mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant={emp.attendance.attendanceStatus === 'full_day' ? 'default' : 'outline'} 
                            onClick={() => { setMarkData({status:'present',conveyance:0,location:'Office'}); setMarkDialog({open:true,employee:emp}); }}
                            className={`h-8 px-2 ${emp.attendance.attendanceStatus === 'full_day' ? 'bg-green-600' : 'text-green-600'}`}>
                            Full
                          </Button>
                          <Button size="sm" variant={emp.attendance.attendanceStatus === 'half_day' ? 'default' : 'outline'}
                            onClick={() => { setMarkData({status:'half_day',conveyance:0,location:'Office'}); setMarkDialog({open:true,employee:emp}); }}
                            className={`h-8 px-2 ${emp.attendance.attendanceStatus === 'half_day' ? 'bg-yellow-600' : 'text-yellow-600'}`}>
                            Half
                          </Button>
                          <Button size="sm" variant={emp.attendance.attendanceStatus === 'absent' ? 'default' : 'outline'}
                            onClick={() => { setMarkData({status:'absent',conveyance:0,location:''}); setMarkDialog({open:true,employee:emp}); }}
                            className={`h-8 px-2 ${emp.attendance.attendanceStatus === 'absent' ? 'bg-red-600' : 'text-red-600'}`}>
                            Absent
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      {bulkEntryMode && (
                        <th className="text-center p-3 font-medium text-gray-600 w-12">
                          <Checkbox 
                            checked={selectedEmployees.length === employees.length}
                            onCheckedChange={toggleSelectAll}
                          />
                        </th>
                      )}
                      <th className="text-left p-3 font-medium text-gray-600">Employee</th>
                      <th className="text-left p-3 font-medium text-gray-600">In/Out</th>
                      <th className="text-left p-3 font-medium text-gray-600">Status</th>
                      <th className="text-right p-3 font-medium text-gray-600">Duty</th>
                      <th className="text-right p-3 font-medium text-gray-600">Conv (₹)</th>
                      <th className="text-left p-3 font-medium text-gray-600">Location</th>
                      {!bulkEntryMode && <th className="text-center p-3 font-medium text-gray-600">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dateAttendance.map((emp) => {
                      const bulkEntry = bulkEntries[emp.id] || {};
                      const isSelected = selectedEmployees.includes(emp.id);
                      return (
                        <tr key={emp.id} className={`hover:bg-gray-50 ${bulkEntryMode && !isSelected ? 'opacity-50' : ''}`}>
                          {bulkEntryMode && (
                            <td className="p-3 text-center">
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={() => toggleEmployeeSelection(emp.id)}
                              />
                            </td>
                          )}
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-xs">
                                {emp.name?.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{emp.name}</p>
                                <p className="text-xs text-gray-500">{emp.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-sm">{emp.attendance.punchIn || '-'} / {emp.attendance.punchOut || '-'}</td>
                          <td className="p-3">
                            {bulkEntryMode ? (
                              <Select 
                                value={bulkEntry.status || 'full_day'} 
                                onValueChange={(v) => updateBulkEntry(emp.id, 'status', v)}
                                disabled={!isSelected}
                              >
                                <SelectTrigger className="w-28 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="full_day">Full Day</SelectItem>
                                  <SelectItem value="half_day">Half Day</SelectItem>
                                  <SelectItem value="leave">Leave</SelectItem>
                                  <SelectItem value="absent">Absent</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                emp.attendance.attendanceStatus === 'full_day' ? 'bg-green-100 text-green-700' :
                                emp.attendance.attendanceStatus === 'half_day' ? 'bg-yellow-100 text-yellow-700' :
                                emp.attendance.attendanceStatus === 'absent' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {emp.attendance.status === 'not-marked' ? 'Not Marked' : 
                                 emp.attendance.attendanceStatus === 'half_day' ? 'Half Day' :
                                 emp.attendance.attendanceStatus === 'absent' ? 'Absent' : 'Full Day'}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-right font-medium text-green-600">₹{emp.attendance.dailyDuty.toLocaleString()}</td>
                          <td className="p-3 text-right">
                            {bulkEntryMode ? (
                              <Input 
                                type="number" 
                                value={bulkEntry.conveyance || 0}
                                onChange={(e) => updateBulkEntry(emp.id, 'conveyance', parseFloat(e.target.value) || 0)}
                                className="w-24 h-8 text-right ml-auto"
                                disabled={!isSelected}
                              />
                            ) : (
                              <span className="font-medium text-blue-600">₹{emp.attendance.conveyance}</span>
                            )}
                          </td>
                          <td className="p-3 text-left">
                            {bulkEntryMode ? (
                              <Input 
                                type="text" 
                                value={bulkEntry.location || ''}
                                onChange={(e) => updateBulkEntry(emp.id, 'location', e.target.value)}
                                className="w-28 h-8"
                                placeholder="Office"
                                disabled={!isSelected}
                              />
                            ) : (
                              <span className="text-sm text-gray-600">{emp.attendance.location || '-'}</span>
                            )}
                          </td>
                          {!bulkEntryMode && (
                            <td className="p-3">
                              <div className="flex justify-center gap-1">
                                <Button size="sm" variant="outline" onClick={() => openMarkDialog(emp)} title="Edit with options">
                                  <Edit size={14} />
                                </Button>
                                <Button size="sm" variant={emp.attendance.attendanceStatus === 'full_day' ? 'default' : 'outline'}
                                  onClick={() => { setMarkData({status:'present',conveyance:0,location:'Office'}); setMarkDialog({open:true,employee:emp}); }}
                                  className={emp.attendance.attendanceStatus === 'full_day' ? 'bg-green-600' : 'text-green-600'}>
                                  <UserCheck size={14} />
                                </Button>
                                <Button size="sm" variant={emp.attendance.attendanceStatus === 'half_day' ? 'default' : 'outline'}
                                  onClick={() => { setMarkData({status:'half_day',conveyance:0,location:'Office'}); setMarkDialog({open:true,employee:emp}); }}
                                  className={emp.attendance.attendanceStatus === 'half_day' ? 'bg-yellow-600' : 'text-yellow-600'}>
                                  ½
                                </Button>
                                <Button size="sm" variant={emp.attendance.attendanceStatus === 'absent' ? 'default' : 'outline'}
                                  onClick={() => { setMarkData({status:'absent',conveyance:0,location:''}); setMarkDialog({open:true,employee:emp}); }}
                                  className={emp.attendance.attendanceStatus === 'absent' ? 'bg-red-600' : 'text-red-600'}>
                                  <UserX size={14} />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Manpower Entries for Selected Date - Admin Only */}
          {isAdmin && (
            <Card data-testid="manpower-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <Users size={18} className="text-purple-600" />
                    Manpower Entries - {new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={openAdminManpowerDialog}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="add-manpower-btn"
                  >
                    <Plus size={14} className="mr-1" /> Add Manpower
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-2 sm:p-4">
                {(() => {
                  const dateManpower = manpowerRecords.filter(m => m.date === selectedDate);
                  if (dateManpower.length === 0) {
                    return (
                      <div className="text-center py-6 text-gray-500">
                        <Users size={32} className="mx-auto text-gray-300 mb-2" />
                        <p className="text-sm">No manpower entries for this date</p>
                        <p className="text-xs text-gray-400 mt-1">Click &quot;Add Manpower&quot; to add entries</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {dateManpower.map((mp) => (
                        <div key={mp.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg" data-testid={`manpower-entry-${mp.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 text-xs font-bold">
                              {mp.name?.split(' ').map(n => n[0]).join('').slice(0,2)}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{mp.name}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-2">
                                <span className="flex items-center gap-1"><MapPin size={10} /> {mp.location}</span>
                                <span className="text-purple-600">• TL: {mp.team_lead_name}</span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Manpower</span>
                              <p className="text-xs text-blue-600 font-medium mt-1">₹{mp.daily_earned || 0} earned</p>
                              {mp.conveyance > 0 && (
                                <p className="text-xs text-green-600">₹{mp.conveyance} conv</p>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleDeleteManpower(mp.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              data-testid={`delete-manpower-${mp.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="mt-2 pt-2 border-t grid grid-cols-3 gap-2 text-xs">
                        <span className="text-gray-600">Total: {dateManpower.length}</span>
                        <span className="text-blue-600 font-medium text-center">
                          Earned: ₹{dateManpower.reduce((sum, m) => sum + (m.daily_earned || 0), 0)}
                        </span>
                        <span className="text-green-600 font-medium text-right">
                          Conv: ₹{dateManpower.reduce((sum, m) => sum + (m.conveyance || 0), 0)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Mark Attendance Dialog */}
      <Dialog open={markDialog.open} onOpenChange={(open) => setMarkDialog({ open, employee: null })}>
        <DialogContent className="max-w-[95vw] sm:max-w-sm mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Mark Attendance</DialogTitle>
            <p className="text-sm text-gray-500 truncate">{markDialog.employee?.name}</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm">Attendance Status</Label>
              <Select value={markData.status} onValueChange={(v) => setMarkData({...markData, status: v, conveyance: v === 'absent' || v === 'leave' ? 0 : markData.conveyance})}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Full Day (Present)</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="leave">Leave (Approved)</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-1"><IndianRupee size={12} /> Conveyance</Label>
                <Input 
                  type="number" 
                  value={markData.conveyance} 
                  onChange={(e) => setMarkData({...markData, conveyance: parseFloat(e.target.value) || 0})}
                  placeholder="₹ Amount"
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-1"><MapPin size={12} /> Location</Label>
                <Input 
                  type="text" 
                  value={markData.location} 
                  onChange={(e) => setMarkData({...markData, location: e.target.value})}
                  placeholder="Location"
                  className="h-9"
                />
              </div>
            </div>
            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg flex justify-between">
              <span><strong>Date:</strong> {selectedDate}</span>
              <span><strong>ID:</strong> {markDialog.employee?.id}</span>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setMarkDialog({ open: false, employee: null })} className="w-full sm:w-auto order-2 sm:order-1">
              Cancel
            </Button>
            <Button onClick={submitAttendance} disabled={submitting} className="bg-[#1E2A5E] w-full sm:w-auto order-1 sm:order-2">
              {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Manpower Dialog - Multi-row Table */}
      <Dialog open={adminManpowerDialog} onOpenChange={setAdminManpowerDialog}>
        <DialogContent className="max-w-[98vw] sm:max-w-4xl mx-1 sm:mx-auto" data-testid="admin-manpower-dialog">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <Users size={18} className="text-purple-600" />
              Add Manpower Entries
            </DialogTitle>
            <p className="text-sm text-gray-500">Add multiple manpower entries at once</p>
          </DialogHeader>
          <div className="py-2">
            {/* Table */}
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-purple-50 border-b">
                    <th className="p-2 text-left font-medium text-purple-800 whitespace-nowrap">Date *</th>
                    <th className="p-2 text-left font-medium text-purple-800 whitespace-nowrap">Team Lead *</th>
                    <th className="p-2 text-left font-medium text-purple-800 whitespace-nowrap">Manpower Name *</th>
                    <th className="p-2 text-left font-medium text-purple-800 whitespace-nowrap">Location *</th>
                    <th className="p-2 text-left font-medium text-purple-800 whitespace-nowrap">Daily Earned *</th>
                    <th className="p-2 text-left font-medium text-purple-800 whitespace-nowrap">Conveyance</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {manpowerRows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50" data-testid={`manpower-row-${idx}`}>
                      <td className="p-1.5">
                        <Input 
                          type="date" 
                          value={row.date}
                          onChange={(e) => updateManpowerRow(idx, 'date', e.target.value)}
                          className="h-8 w-32 text-xs"
                        />
                      </td>
                      <td className="p-1.5">
                        <Select value={row.team_lead_id} onValueChange={(v) => updateManpowerRow(idx, 'team_lead_id', v)}>
                          <SelectTrigger className="h-8 w-36 text-xs">
                            <SelectValue placeholder="Select TL" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamLeads.map((tl) => (
                              <SelectItem key={tl.id} value={tl.id}>{tl.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1.5">
                        <Input 
                          type="text" 
                          value={row.name}
                          onChange={(e) => updateManpowerRow(idx, 'name', e.target.value)}
                          placeholder="Worker name"
                          className="h-8 w-32 text-xs"
                        />
                      </td>
                      <td className="p-1.5">
                        <Input 
                          type="text" 
                          value={row.location}
                          onChange={(e) => updateManpowerRow(idx, 'location', e.target.value)}
                          placeholder="Location"
                          className="h-8 w-28 text-xs"
                        />
                      </td>
                      <td className="p-1.5">
                        <Input 
                          type="number" 
                          value={row.daily_earned}
                          onChange={(e) => updateManpowerRow(idx, 'daily_earned', parseFloat(e.target.value) || 0)}
                          className="h-8 w-24 text-xs"
                        />
                      </td>
                      <td className="p-1.5">
                        <Input 
                          type="number" 
                          value={row.conveyance}
                          onChange={(e) => updateManpowerRow(idx, 'conveyance', parseFloat(e.target.value) || 0)}
                          className="h-8 w-24 text-xs"
                        />
                      </td>
                      <td className="p-1.5">
                        {manpowerRows.length > 1 && (
                          <Button 
                            size="sm" variant="ghost" 
                            onClick={() => removeManpowerRow(idx)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add Row + Summary */}
            <div className="flex items-center justify-between mt-3">
              <Button 
                size="sm" variant="outline" onClick={addManpowerRow}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                data-testid="add-manpower-row-btn"
              >
                <Plus size={14} className="mr-1" /> Add Row
              </Button>
              <div className="text-xs text-purple-600 bg-purple-50 px-3 py-1.5 rounded-full">
                <strong>{manpowerRows.filter(r => r.name.trim()).length}</strong> entries | 
                Total: <strong>₹{manpowerRows.reduce((sum, r) => sum + (r.daily_earned || 0) + (r.conveyance || 0), 0).toLocaleString()}</strong>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setAdminManpowerDialog(false)} className="w-full sm:w-auto order-2 sm:order-1">
              Cancel
            </Button>
            <Button 
              onClick={submitAdminManpower} 
              disabled={addingManpower || teamLeads.length === 0} 
              className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto order-1 sm:order-2"
              data-testid="submit-manpower-btn"
            >
              {addingManpower ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-1" />}
              Add All ({manpowerRows.filter(r => r.name.trim()).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Attendance Excel Dialog */}
      <Dialog open={uploadAttendanceDialog} onOpenChange={setUploadAttendanceDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg mx-2 sm:mx-auto" data-testid="upload-attendance-dialog">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-emerald-600" />
              Upload Attendance (Excel)
            </DialogTitle>
            <p className="text-sm text-gray-500">Upload monthly attendance data via Excel file</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Step 1: Download Template */}
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm font-medium text-emerald-800 mb-2">Step 1: Download Template</p>
              <p className="text-xs text-emerald-600 mb-3">Template has pre-filled Employee IDs. Fill Date, Status, Location, Conveyance.</p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadAttendanceTemplate}
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-100"
                data-testid="download-attendance-template-btn"
              >
                <Download size={14} className="mr-1" /> Download Template
              </Button>
            </div>

            {/* Step 2: Upload */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Step 2: Upload Filled Excel</p>
              <p className="text-xs text-blue-600 mb-3">Already marked dates will be skipped. Daily Duty is auto-calculated.</p>
              <label
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                  uploadingAttendance
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                data-testid="upload-attendance-file-btn"
              >
                {uploadingAttendance ? (
                  <><Loader2 size={14} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={14} /> Choose File & Upload</>
                )}
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleUploadAttendance}
                  disabled={uploadingAttendance}
                  className="hidden"
                  data-testid="attendance-file-input"
                />
              </label>
            </div>

            {/* Results */}
            {attendanceUploadResult && (
              <div className="p-3 bg-gray-50 rounded-lg border" data-testid="attendance-upload-result">
                <p className="text-sm font-medium mb-2">Upload Results</p>
                <div className="grid grid-cols-4 gap-2 text-center mb-2">
                  <div className="p-2 bg-green-100 rounded">
                    <CheckCircle size={16} className="mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold text-green-700">{attendanceUploadResult.marked}</p>
                    <p className="text-[10px] text-green-600">Marked</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded">
                    <CheckCircle size={16} className="mx-auto text-blue-600 mb-1" />
                    <p className="text-lg font-bold text-blue-700">{attendanceUploadResult.updated || 0}</p>
                    <p className="text-[10px] text-blue-600">Conv. Updated</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded">
                    <AlertTriangle size={16} className="mx-auto text-yellow-600 mb-1" />
                    <p className="text-lg font-bold text-yellow-700">{attendanceUploadResult.skipped}</p>
                    <p className="text-[10px] text-yellow-600">Skipped</p>
                  </div>
                  <div className="p-2 bg-red-100 rounded">
                    <XCircle size={16} className="mx-auto text-red-600 mb-1" />
                    <p className="text-lg font-bold text-red-700">{attendanceUploadResult.errors?.length || 0}</p>
                    <p className="text-[10px] text-red-600">Errors</p>
                  </div>
                </div>
                {attendanceUploadResult.errors?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1 mt-2">
                    {attendanceUploadResult.errors.map((err, i) => (
                      <div key={i} className="flex gap-2 text-red-600 bg-red-50 p-1.5 rounded">
                        <span className="font-medium shrink-0">Row {err.row}:</span>
                        <span>{err.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info */}
            <div className="text-xs text-gray-500 space-y-1">
              <p><strong>Status options:</strong> Present, Half Day, Absent, Leave</p>
              <p><strong>Date format:</strong> DD-MM-YYYY or DD/MM/YYYY</p>
              <p><strong>Note:</strong> Pehle se marked attendance ke liye Excel ka conveyance (agar &gt; 0 hai) auto-update ho jaayega. Already non-zero conveyance values overwrite NAHI honge.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadAttendanceDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Manpower Excel Dialog */}
      <Dialog open={uploadManpowerDialog} onOpenChange={setUploadManpowerDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg mx-2 sm:mx-auto" data-testid="upload-manpower-dialog">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-purple-600" />
              Upload Manpower (Excel)
            </DialogTitle>
            <p className="text-sm text-gray-500">Upload manpower entries via Excel file</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Step 1: Download Template */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm font-medium text-purple-800 mb-2">Step 1: Download Template</p>
              <p className="text-xs text-purple-600 mb-3">Template has pre-filled Team Lead IDs. Fill Name, Date, Location, Daily Earned, Conveyance.</p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadManpowerTemplate}
                className="border-purple-400 text-purple-700 hover:bg-purple-100"
                data-testid="download-manpower-template-btn"
              >
                <Download size={14} className="mr-1" /> Download Template
              </Button>
            </div>

            {/* Step 2: Upload */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-2">Step 2: Upload Filled Excel</p>
              <p className="text-xs text-blue-600 mb-3">Each row creates a new manpower entry under the specified Team Lead.</p>
              <label
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                  uploadingManpower
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
                data-testid="upload-manpower-file-btn"
              >
                {uploadingManpower ? (
                  <><Loader2 size={14} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={14} /> Choose File & Upload</>
                )}
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleUploadManpower}
                  disabled={uploadingManpower}
                  className="hidden"
                  data-testid="manpower-file-input"
                />
              </label>
            </div>

            {/* Results */}
            {manpowerUploadResult && (
              <div className="p-3 bg-gray-50 rounded-lg border" data-testid="manpower-upload-result">
                <p className="text-sm font-medium mb-2">Upload Results</p>
                <div className="grid grid-cols-2 gap-2 text-center mb-2">
                  <div className="p-2 bg-green-100 rounded">
                    <CheckCircle size={16} className="mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold text-green-700">{manpowerUploadResult.added}</p>
                    <p className="text-[10px] text-green-600">Added</p>
                  </div>
                  <div className="p-2 bg-red-100 rounded">
                    <XCircle size={16} className="mx-auto text-red-600 mb-1" />
                    <p className="text-lg font-bold text-red-700">{manpowerUploadResult.errors?.length || 0}</p>
                    <p className="text-[10px] text-red-600">Errors</p>
                  </div>
                </div>
                {manpowerUploadResult.errors?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1 mt-2">
                    {manpowerUploadResult.errors.map((err, i) => (
                      <div key={i} className="flex gap-2 text-red-600 bg-red-50 p-1.5 rounded">
                        <span className="font-medium shrink-0">Row {err.row}:</span>
                        <span>{err.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info */}
            <div className="text-xs text-gray-500 space-y-1">
              <p><strong>Required:</strong> Manpower Name, Team Lead ID, Date, Location, Daily Earned</p>
              <p><strong>Date format:</strong> DD-MM-YYYY or DD/MM/YYYY</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadManpowerDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
